import React, { useState, useEffect } from "react";
import { AlertTriangle, RotateCcw, LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useGame } from "../context/GameContext";
import socketService from "../services/socketService";

const AVATARS = [
  { id: 'renard', image: '/images/renard.png', name: 'Renard', color: '#FF6B35' },
  { id: 'loup', image: '/images/loup.png', name: 'Loup', color: '#4A5568' },
  { id: 'lion', image: '/images/lion.png', name: 'Lion', color: '#D69E2E' },
  { id: 'panda', image: '/images/panda.png', name: 'Panda', color: '#2D3748' },
  { id: 'hibou', image: '/images/hibou.png', name: 'Hibou', color: '#805AD5' },
  { id: 'dragon', image: '/images/dragon.png', name: 'Dragon', color: '#38A169' },
  { id: 'poulpe', image: '/images/poulpe.png', name: 'Poulpe', color: '#E53E3E' },
  { id: 'aigle', image: '/images/aigle.png', name: 'Aigle', color: '#2B6CB0' },
  { id: 'tigre', image: '/images/tigre.png', name: 'Tigre', color: '#DD6B20' },
  { id: 'papillon', image: '/images/papillon.png', name: 'Papillon', color: '#9F7AEA' },
  { id: 'chat', image: '/images/chat.png', name: 'Chat', color: '#F6AD55' },
  { id: 'cygne', image: '/images/cygne.png', name: 'Cygne', color: '#63B3ED' },
  { id: 'lapin', image: '/images/lapin.png', name: 'Lapin', color: '#FC8181' },
  { id: 'tortue', image: '/images/tortue.png', name: 'Tortue', color: '#68D391' },
];

const Home = () => {
  const [roomInput, setRoomInput] = useState("");
  const [pseudoInput, setPseudoInput] = useState(() => {
    return localStorage.getItem("savedPseudo") || "";
  });
  const [hasActiveSession, setHasActiveSession] = useState(false);
  const [savedRoomId, setSavedRoomId] = useState("");
  const [selectedAvatar, setSelectedAvatar] = useState(() => {
    return localStorage.getItem("selectedAvatar") || null;
  });
  const { joinRoom, setPseudo, prepareReconnect, joinAsSpectator } = useGame();
  const navigate = useNavigate();

  useEffect(() => {
    if (socketService.hasActiveSession()) {
      setHasActiveSession(true);
      setSavedRoomId(socketService.getSavedRoomId());
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const roomParam = params.get("room");
    if (roomParam) {
      setRoomInput(roomParam.toUpperCase());
      // Clean URL without reloading
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const handleSelectAvatar = (avatarId) => {
    setSelectedAvatar(avatarId);
    localStorage.setItem("selectedAvatar", avatarId);
  };

  const handleResumeGame = () => {
    prepareReconnect();
    const success = socketService.manualReconnect();
    if (success) {
      navigate("/room/" + socketService.getSavedRoomId());
    } else {
      alert("Impossible de reprendre la partie. Les données de session sont invalides.");
      setHasActiveSession(false);
    }
  };

  const handleQuitAndCreateNew = () => {
    socketService.clearSession();
    setHasActiveSession(false);
    const newRoomId = Math.random().toString(36).substring(2, 8).toUpperCase();
    localStorage.setItem("selectedAvatar", selectedAvatar);
    localStorage.setItem("savedPseudo", pseudoInput.trim());
    joinRoom(newRoomId);
    setTimeout(() => setPseudo(pseudoInput.trim()), 300);
    navigate("/room/" + newRoomId);
  };

  const handleJoinRoom = (e) => {
    e.preventDefault();
    if (roomInput.trim() && selectedAvatar && pseudoInput.trim()) {
      localStorage.setItem("selectedAvatar", selectedAvatar);
      localStorage.setItem("savedPseudo", pseudoInput.trim());
      joinRoom(roomInput.trim());
      setTimeout(() => setPseudo(pseudoInput.trim()), 300);
      navigate("/room/" + roomInput.trim());
    }
  };

  const handleCreateRoom = () => {
    if (hasActiveSession) return;
    if (!selectedAvatar || !pseudoInput.trim()) return;
    const newRoomId = Math.random().toString(36).substring(2, 8).toUpperCase();
    localStorage.setItem("selectedAvatar", selectedAvatar);
    localStorage.setItem("savedPseudo", pseudoInput.trim());
    joinRoom(newRoomId);
    setTimeout(() => setPseudo(pseudoInput.trim()), 300);
    navigate("/room/" + newRoomId);
  };

  const canInteract = !hasActiveSession && !!selectedAvatar && !!pseudoInput.trim();

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl p-8 max-w-3xl w-full">
        {/* Header */}
        <h1 className="sr-only">Guess Party</h1>
        <div className="flex justify-center mb-2">
          <img
            src="/brand/logo-lockup-light.png"
            alt="Guess Party"
            className="h-24 sm:h-28 w-auto"
          />
        </div>
        <p className="text-center text-gray-600 mb-6">
          Le devine-tête multijoueur en ligne
        </p>

        {/* Bandeau session active */}
        {hasActiveSession && (
          <div className="mb-6 p-4 bg-yellow-50 border-2 border-yellow-400 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle size={24} className="text-yellow-600" />
              <div className="flex-1">
                <h3 className="font-bold text-yellow-900 mb-1">
                  Partie en cours détectée
                </h3>
                <p className="text-sm text-yellow-800 mb-3">
                  Vous avez une partie en cours (Room:{" "}
                  <span className="font-bold">{savedRoomId}</span>)
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={handleResumeGame}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
                  >
                    <RotateCcw size={16} className="inline mr-1" /> Reprendre la partie
                  </button>
                  <button
                    onClick={handleQuitAndCreateNew}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
                  >
                    <LogOut size={16} className="inline mr-1" /> Quitter et créer une nouvelle
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Layout horizontal : avatars | actions */}
        <div className="flex flex-col md:flex-row gap-6">
          {/* Colonne gauche : avatars */}
          {!hasActiveSession && (
            <div className="md:w-1/2">
              <h3 className="text-lg font-semibold text-center text-gray-800 mb-4">
                Choisissez votre avatar
              </h3>
              <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-4 gap-2 justify-items-center">
                {AVATARS.map((av) => {
                  const isSelected = selectedAvatar === av.id;
                  return (
                    <button
                      key={av.id}
                      onClick={() => handleSelectAvatar(av.id)}
                      title={av.name}
                      className={`transition-all duration-200 ease-in-out rounded-full overflow-hidden ${
                        isSelected ? "ring-3 ring-purple-500 ring-offset-2 scale-110" : "hover:scale-108"
                      }`}
                      style={{
                        width: 72,
                        height: 72,
                        backgroundColor: "#ffffff",
                        border: isSelected
                          ? "3px solid #7C3AED"
                          : "3px solid transparent",
                        boxShadow: isSelected
                          ? "0 0 0 3px #C4B5FD"
                          : "0 1px 3px rgba(0,0,0,0.1)",
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.transform = "scale(1.08)";
                          e.currentTarget.style.boxShadow =
                            "0 4px 12px rgba(0,0,0,0.15)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.transform = "scale(1)";
                          e.currentTarget.style.boxShadow =
                            "0 1px 3px rgba(0,0,0,0.1)";
                        }
                      }}
                    >
                      <img
                        src={av.image}
                        alt={av.name}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  );
                })}
              </div>
              <div className="mt-4">
                <input
                  type="text"
                  placeholder="Votre pseudo"
                  value={pseudoInput}
                  onChange={(e) => setPseudoInput(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-center"
                  maxLength={20}
                />
              </div>
            </div>
          )}

          {/* Colonne droite : boutons et code */}
          <div className={hasActiveSession ? "w-full" : "md:w-1/2 flex flex-col justify-center"}>
            <div className="space-y-4">
              <button
                onClick={handleCreateRoom}
                disabled={!canInteract}
                className={`w-full font-semibold py-3 px-4 rounded-lg transition duration-200 transform ${
                  !canInteract
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-purple-600 hover:bg-purple-700 text-white hover:scale-105"
                }`}
              >
                Créer une nouvelle partie
              </button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">OU</span>
                </div>
              </div>

              <form onSubmit={handleJoinRoom}>
                <input
                  type="text"
                  placeholder="Code de la room"
                  value={roomInput}
                  onChange={(e) => setRoomInput(e.target.value.toUpperCase())}
                  disabled={hasActiveSession}
                  className={`w-full px-4 py-3 border rounded-lg mb-3 ${
                    hasActiveSession
                      ? "bg-gray-100 text-gray-500 cursor-not-allowed border-gray-300"
                      : "border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  }`}
                  maxLength={6}
                />
                <button
                  type="submit"
                  disabled={!canInteract}
                  className={`w-full font-semibold py-3 px-4 rounded-lg transition duration-200 ${
                    !canInteract
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-indigo-600 hover:bg-indigo-700 text-white"
                  }`}
                >
                  Rejoindre une partie
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (roomInput.trim()) {
                      joinAsSpectator(roomInput.trim());
                      navigate("/room/" + roomInput.trim());
                    }
                  }}
                  disabled={hasActiveSession || !roomInput.trim()}
                  className="w-full text-sm text-gray-500 hover:text-purple-600 transition duration-200 mt-2"
                >
                  Rejoindre en spectateur
                </button>
              </form>
            </div>

            <div className="mt-6 p-4 bg-purple-50 rounded-lg">
              <h3 className="font-semibold text-purple-800 mb-2">
                Comment jouer ?
              </h3>
              <ol className="text-sm text-gray-700 space-y-1 list-decimal list-inside">
                <li>Rejoignez une room et choisissez un pseudo</li>
                <li>Chaque joueur entre un personnage</li>
                <li>Devinez votre personnage à votre tour</li>
                <li>Utilisez Discord pour les questions vocales</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
